import { useGetRelationsInByIdQuery, useGetObjectByIdQuery, useGetRelationsOutByIdQuery, RELATION_TYPE } from '../../services/objects'
import { Block } from 'baseui/block';
import { AiOutlineArrowRight } from 'react-icons/ai'
import { AutomationObjectsSelector } from '../../automationObjects/automationObjectsSelector'
import { CardWithAlarm } from '../../automationObjects/components/Grid'
//import RelationsList from '../../automationObjects/components/RelationsView/CreateRelation'
import Xarrow, { useXarrow, anchorType } from 'react-xarrows';
import { Button } from 'baseui/button';
import { useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { VscEdit, VscTrash } from 'react-icons/vsc';
import { Plus } from 'baseui/icon';
import { LabelSmall, LabelXSmall } from 'baseui/typography';
import { DeleteRelationDialog, CreateRelationDialog } from './';
import { FormattedMessage, useIntl } from 'react-intl';
import { Banner } from 'baseui/banner';
import useIsMobile from '../../hooks/useIsMobile/useIsMobile';
import { useMediaQuery } from '../../hooks';
import { AccessRoles, useHasRole } from '../../hooks/useHasRole';
import { ValueDisplay } from '../../automationObjects/components/ValueDisplay';

interface RelationsViewFlowProps {
    objectId: string,
    resourceGroupId: string,
    relationType: keyof typeof RELATION_TYPE
}
interface ArrowProps {
    startAnchor: anchorType,
    endAnchor: anchorType,
}
function RelationsViewFlow({ objectId, resourceGroupId, relationType }: RelationsViewFlowProps) {

    const hasAccess = useHasRole(AccessRoles.Object.Write);

    const [css, theme] = useStyletron();
    const updateXarrow = useXarrow();
    const isLargeScreen = useMediaQuery(theme.mediaQuery.large.replace('@media ', ''));
    const oid = objectId;
    const type = relationType;
    const { data: objData, isLoading: objLoading, isSuccess: objIsSucess } = useGetObjectByIdQuery({ id: objectId, resourceGroupId: resourceGroupId }, { skip: !objectId });
    const { data: nodesIn, isLoading, isSuccess } = useGetRelationsInByIdQuery({ objectId: oid, type: type, resourceGroupId: resourceGroupId }, { skip: !objectId });
    const { data: nodesOut, isLoading: isLoadingOut, isSuccess: isSuccessOut } = useGetRelationsOutByIdQuery({ objectId: oid, type: type, resourceGroupId: resourceGroupId }, { skip: !objectId });
    const [edit, setEdit] = useState(false);

    const hasNodesIn = (nodesIn?.length || 0) > 0;
    const hasNodesOut = (nodesOut?.length || 0) > 0;

    useEffect(() => {
        updateXarrow();
    }, [objData, nodesIn, nodesOut]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            updateXarrow();
        }, 3000);
        return () => { clearTimeout(timeout) };
    }, []);

    const noNodes = !hasNodesIn && !hasNodesOut

    const anchors: ArrowProps = isLargeScreen ? { startAnchor: 'right', endAnchor: 'left'}  : { startAnchor: 'auto', endAnchor: 'auto'};
    const nodesInCnt = nodesIn?.length || 0;
    return (
        <Block display='flex' flexDirection='column' gridRowGap='25px' justifyContent='space-between'>
            <Block display='flex' justifyContent='space-between'>
                {(noNodes && !isLoading && !isLoadingOut) ? <Banner kind="info">
                <FormattedMessage id="Relations.NoNodesInfoText" defaultMessage="This App has relations yet 😥 Click the {buttonIcon} button to add relations and see what is going on in your facilities"
                 values={{
                    buttonIcon: <VscEdit></VscEdit>
                }} />
                    </Banner> : <span></span>}
                <Button disabled={!hasAccess} isSelected={edit} kind='tertiary' shape='circle' onClick={() => setEdit(!edit)}><VscEdit></VscEdit></Button>
            </Block>
            <Block display='flex' flexDirection='row' gridGap='25px' alignItems='center' flexWrap >
                <>
                    <ItemsContainer toObject={objData} relationType={relationType} edit={edit} items={nodesIn}></ItemsContainer>
                    <AiOutlineArrowRight size='3em' color='transparent'></AiOutlineArrowRight>
                </>
                {(!noNodes || edit)&&
                    <ItemsContainer idPrefix='center' items={[objData]}></ItemsContainer>
                }
                <>
                    <AiOutlineArrowRight size='3em' color='transparent'></AiOutlineArrowRight>
                    <ItemsContainer fromObject={objData} relationType={relationType} edit={edit} items={nodesOut}></ItemsContainer>
                </>

                {nodesIn?.map((line, i) => (
                    <Xarrow dashness={{ strokeLen: 10, nonStrokeLen: 5, animation: 0.5 }} labels={<Labels  fromObjectId={line.id} toObjectId={objData.id} resourceGroupId={objData.resourceGroupId} edit={edit} relationType={relationType} value={line.actPower? line.actPower : ''} dimension={line.dimension&&line.actPower?line.dimension==='kWh'?'kW': line.dimension==='MWh'?'MW': line.dimension==='Wh'?'W': line.dimension : ''} />}  color={theme.colors.positive} strokeWidth={2} key={i} start={line.id} end={objData.id} {...anchors} />
                ))}
                {nodesOut?.map((line, i) => (
                    <Xarrow dashness={{ strokeLen: 10, nonStrokeLen: 5, animation: 0.5 }} labels={<Labels fromObjectId={objData.id} toObjectId={line.id} resourceGroupId={objData.resourceGroupId} edit={edit} relationType={relationType} value={line.actPower? line.actPower : ''} dimension={line.dimension&&line.actPower?line.dimension==='kWh'?'kW': line.dimension==='MWh'?'MW': line.dimension==='Wh'?'W': line.dimension : ''}/>}  color={theme.colors.accent} strokeWidth={2} key={i} start={objData.id} end={line.id} {...anchors} />
                ))}

            </Block>

        </Block>)
}

export default RelationsViewFlow

const Labels = ({ edit, fromObjectId, toObjectId, resourceGroupId, relationType, value, dimension }) => {
    const [css, theme] = useStyletron();
    const [isOpen, setIsOpen] = useState(false);
    const intl = useIntl();

    if (!edit)
        return (
        <Block padding={theme.sizing.scale100} backgroundColor={theme.colors.backgroundPrimary}  className={css({style: { zIndex: 999 }})}>
            <LabelXSmall>{intl.formatMessage({ id: "Relations." + relationType, defaultMessage: relationType })}</LabelXSmall>
            <ValueDisplay  value={value} dimension={dimension} small></ValueDisplay>
        </Block>);
    return (
        <>
            <Button title='Delete Relation' size='compact' shape='circle' kind='secondary' onClick={() => setIsOpen(true)}><VscTrash></VscTrash></Button>
            <DeleteRelationDialog fromObjectId={fromObjectId} toObjectId={toObjectId} resourceGroupId={resourceGroupId} relationType={relationType} isOpen={isOpen} setIsOpen={setIsOpen} ></DeleteRelationDialog>
        </>
    )
}

function ItemsContainer({ items, edit, fromObject, toObject, resourceGroupId, relationType }: any) {
    const [isOpen, setIsOpen] = useState(false);
    const intl = useIntl();

    return (
        <Block display='flex' flexDirection='column' gridRowGap='25px' justifyContent='space-between'>
            {edit &&
                <>
                    <Button kind='secondary' size='compact' onClick={() => setIsOpen(true)}>
                        <Block display='flex' flexDirection='column' alignItems='center' gridGap='10px'>
                            <Plus size='2em'></Plus>
                            <span><FormattedMessage id="Relations.AddRelation" defaultMessage="Add {relationType} relation" values={{
                                relationType: <b>{intl.formatMessage({ id: "Relations." + relationType, defaultMessage: relationType })}</b>,
                            }} /></span>
                        </Block>
                    </Button>
                    <CreateRelationDialog sourceObject={fromObject} tragetObject={toObject} relationType={relationType} isOpen={isOpen} setIsOpen={setIsOpen} ></CreateRelationDialog>
                </>
            }
            {items?.map((item: any, index: number) => (
                <div key={item.id} id={item.id}>
                    <CardWithAlarm objectId={item.id}>
                        <AutomationObjectsSelector tag={item.schema} state={item}></AutomationObjectsSelector>
                    </CardWithAlarm>
                </div>
            ))
            }
        </Block>
    )
}

